/**
 * ScrollToContentOnRoute — on every route change, scroll the window so
 * the main content header sits at the top of the viewport, skipping the
 * pre-content chrome (locality banner, tagline hero, explore chip strip,
 * translate bar). Fixes the "user has to scroll 500-700px past the
 * banners on every nav click" pain point.
 *
 * Behaviour:
 *   - On the root path (`/`) we scroll to the very top so the Home hero
 *     is preserved.
 *   - On any other path we scroll to the top of <main>.
 *   - Respects the browser's back/forward scroll restoration (only fires
 *     on programmatic navigation / new pushes).
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

export default function ScrollToContentOnRoute() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType(); // "POP" (back/forward) | "PUSH" | "REPLACE"
  const first = useRef(true);

  useEffect(() => {
    // Skip the very first render — let the browser do its thing (deep-link,
    // anchor jump, restored scroll position, etc.).
    if (first.current) {
      first.current = false;
      return;
    }
    // Honour anchor jumps (#foo) — let the browser scroll to the target.
    if (hash) return;
    // Honour back/forward — the browser restores the previous scroll.
    if (navType === "POP") return;

    // Home stays anchored at the very top so users see the hero.
    if (pathname === "/") {
      window.scrollTo({ top: 0, behavior: "instant" in window ? "auto" : "auto" });
      return;
    }

    // For every other route, land the user right on the page's content
    // header (the <main> element sits after all the pre-content banners).
    const main = document.querySelector("main");
    if (!main) {
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }
    const top = main.getBoundingClientRect().top + window.scrollY - 8;
    window.scrollTo({ top: Math.max(0, top), behavior: "auto" });
  }, [pathname, hash, navType]);

  return null;
}
